import { Box, Stack, Typography } from '@mui/material';
import { CardSection, CardSubSection, useIsMobile } from '@side/pantry';
import type { ReactNode } from 'react';
import { GlossaryTooltip } from '../components/GlossaryTooltip';
import { FilterValuesFooter } from '../FilterValuesFooter/FilterValuesFooter';
import { NoResultsOverlay } from '../NoResultsOverlay/NoResultsOverlay';
import {
  useReportsRole,
  useRoleCanSee,
} from '../ReportsConfiguration/useReportsRole';
import {
  BORDER_ROW,
  BreakdownGrid,
  formatSignedCurrency,
  getGridTemplateColumns,
  highlightedCellSx,
  labelCellSx,
  PayoutValueSkeleton,
  valueCellSx,
} from './BreakdownGrid';
import { usePayoutSummary } from './PayoutSummary.helpers';

interface GciRowProps {
  /** Same count as BreakdownGrid value columns (Brand / Team / Agent). */
  valueColumns: number;
  label: ReactNode;
  value: number | null | undefined;
  isLoading: boolean;
  isHighlight?: boolean;
  appendCents?: boolean;
  glossaryLabel?: string;
}

function GciRow({
  valueColumns,
  label,
  value,
  isLoading,
  isHighlight,
  appendCents = false,
  glossaryLabel,
}: GciRowProps) {
  const isMobile = useIsMobile('md');
  const labelContent =
    glossaryLabel != null ? (
      <GlossaryTooltip label={glossaryLabel}>
        <Typography variant={isHighlight ? 'h5' : 'body2'} component="span">
          {label}
        </Typography>
      </GlossaryTooltip>
    ) : (
      <Typography variant={isHighlight ? 'h5' : 'body2'}>{label}</Typography>
    );

  const valueCellGridSx = isHighlight
    ? { ...highlightedCellSx, justifyContent: 'center' }
    : valueCellSx({ justifyContent: 'center' });

  const emptyValuePlaceholders =
    valueColumns > 1 ? (
      <>
        <Box key="gci-brand-col" aria-hidden sx={valueCellGridSx} />
        {valueColumns > 2 ? (
          <Box key="gci-team-col" aria-hidden sx={valueCellGridSx} />
        ) : null}
      </>
    ) : null;

  const valueTypographyVariant = isHighlight ? 'h5' : 'body2';
  let payoutValueContent = (
    <Typography
      variant={valueTypographyVariant}
      sx={{ color: 'text.disabled' }}
    >
      –
    </Typography>
  );

  if (isLoading) {
    payoutValueContent = <PayoutValueSkeleton />;
  } else if (typeof value === 'number') {
    payoutValueContent = (
      <Typography variant={valueTypographyVariant}>
        {formatSignedCurrency(value, appendCents)}
      </Typography>
    );
  }

  return (
    <Box
      sx={{
        display: 'grid',
        width: '100%',
        gridTemplateColumns: getGridTemplateColumns(
          isMobile ? 1 : valueColumns,
        ),
      }}
    >
      <Box sx={isHighlight ? highlightedCellSx : labelCellSx()}>
        {labelContent}
      </Box>
      {isMobile ? null : emptyValuePlaceholders}
      <Box sx={valueCellGridSx}>{payoutValueContent}</Box>
    </Box>
  );
}

export function PayoutSummary({ viewReportAs }: { viewReportAs: string }) {
  const {
    weightedAdjustedGrossCommission,
    weightedStartingGrossCommission,
    preSplitDeductions,
    agentGrossShare,
    agentFeesReceived,
    agentFeesPaid,
    agentNetShare,
    brandGrossShare,
    brandFeesReceived,
    brandFeesPaid,
    brandNetShare,
    teamGrossShare,
    teamFeesReceived,
    teamFeesPaid,
    teamNetShare,
    noResults,
    isLoading: isPayoutSummaryLoading,
  } = usePayoutSummary({ viewReportAs });
  const { isPartner, isLoading: isReportsRoleLoading } = useReportsRole();
  const { roleCanSee } = useRoleCanSee();
  const isLoading = isPayoutSummaryLoading || isReportsRoleLoading;
  // Brand column is only for partners — team leads see team data but not brand
  const showBrand = isPartner && roleCanSee('brand');
  const showTeam = roleCanSee('team');
  const valueColumns = 1 + (showBrand ? 1 : 0) + (showTeam ? 1 : 0);

  return (
    <CardSection
      cardTitle="Payout Summary"
      id="payout-summary-card-section"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        '& .MuiCollapse-root, & .MuiCollapse-wrapper': { height: '100%' },
      }}
    >
      <CardSubSection sx={{ height: '100%', p: 0 }}>
        <Stack sx={{ gap: 3, justifyContent: 'space-between', height: '100%' }}>
          <NoResultsOverlay
            isActive={noResults && !isLoading}
            message="No earnings match the current filters"
          >
            <Box
              sx={{
                // TODO: this border bottom should be removed when the filter values footer is removed
                borderBottom: `1px solid ${BORDER_ROW}`,
              }}
            >
              <GciRow
                valueColumns={valueColumns}
                label="GCI"
                value={weightedStartingGrossCommission}
                isLoading={isLoading}
              />
              <GciRow
                valueColumns={valueColumns}
                label="Pre-Split Deductions"
                glossaryLabel="Pre-Split Deductions"
                value={preSplitDeductions}
                isLoading={isLoading}
              />
              <GciRow
                valueColumns={valueColumns}
                label="Post Referral GCI"
                glossaryLabel="Post-Referral GCI"
                value={weightedAdjustedGrossCommission}
                isLoading={isLoading}
                isHighlight
              />
              <BreakdownGrid
                isLoading={isLoading}
                showBrand={showBrand}
                showTeam={showTeam}
                agentGrossShare={agentGrossShare}
                agentFeesReceived={agentFeesReceived}
                agentFeesPaid={agentFeesPaid}
                agentNetShare={agentNetShare}
                brandGrossShare={brandGrossShare}
                brandFeesReceived={brandFeesReceived}
                brandFeesPaid={brandFeesPaid}
                brandNetShare={brandNetShare}
                teamGrossShare={teamGrossShare}
                teamFeesReceived={teamFeesReceived}
                teamFeesPaid={teamFeesPaid}
                teamNetShare={teamNetShare}
              />
            </Box>
          </NoResultsOverlay>
          <Box sx={{ px: 3, pb: 3.5, pt: 0 }}>
            <FilterValuesFooter variant="compensation" />
          </Box>
        </Stack>
      </CardSubSection>
    </CardSection>
  );
}
